import { useEffect, useRef, type ReactNode } from 'react';
import styles from './Drawer.module.css';

type Props = {
  open: boolean;
  onClose: () => void;
  side?: 'right' | 'left';
  title?: string;
  children?: ReactNode;
  ariaLabel?: string;
};

export default function Drawer({
  open,
  onClose,
  side = 'right',
  title,
  children,
  ariaLabel,
}: Props) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    const previousActive = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
      previousActive?.focus?.();
    };
  }, [open, onClose]);

  return (
    <div className={`${styles.root} ${open ? styles.open : ''}`} aria-hidden={!open}>
      <div className={styles.backdrop} onClick={onClose} />
      <div
        ref={panelRef}
        className={`${styles.panel} ${styles[`side-${side}`]}`}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel ?? title}
        tabIndex={-1}
      >
        {title && (
          <div className={styles.header}>
            <h2 className={styles.title}>{title}</h2>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={onClose}
              aria-label="閉じる"
            >
              ×
            </button>
          </div>
        )}
        <div className={styles.body}>{children}</div>
      </div>
    </div>
  );
}
